// Posts du hub You (GET /api/posts), avec cache localStorage. Rien ici ne doit
// casser l'app : serveur muet, JSON tordu ou localStorage refuse laissent le
// cache en place.
import { useSyncExternalStore } from 'react';
import { API_URL } from '../config/api';

export const POSTS_CACHE_KEY = 'upscale.posts.cache';
export const POSTS_LAST_SEEN_KEY = 'upscale.posts.lastSeenAt';
export const POSTS_LAST_TOAST_KEY = 'upscale.posts.lastToastVersion';

// Les types qui alimentent le fil What's new, donc le point rouge. known_issue
// et faq vivent dans l'aide : ils ne reveillent jamais de pastille.
export const FEED_TYPES = ['news', 'live', 'release'];

const store = { posts: [], lastSeenAt: null, loaded: false };
let initialized = false;
let version = 0;
let inflight = null;
const listeners = new Set();

// Snapshot memoise : useSyncExternalStore compare par identite, donc l'objet doit
// rester le meme tant que le store n'a pas bouge, sinon le rendu boucle.
let snapshot = null;
let snapshotVersion = -1;

function safeGet(key) {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSet(key, value) {
  try {
    localStorage.setItem(key, value);
  } catch {
    // localStorage indisponible : le store memoire suffit.
  }
}

function emit() {
  version += 1;
  for (const fn of listeners) fn();
}

// Hydratation paresseuse. Elle n'emet PAS : elle est appelee depuis getSnapshot,
// et bouger la version pendant un rendu ferait boucler useSyncExternalStore.
function ensureInit() {
  if (initialized) return;
  initialized = true;
  const raw = safeGet(POSTS_CACHE_KEY);
  if (raw) {
    try {
      const cached = JSON.parse(raw);
      if (Array.isArray(cached?.posts)) {
        store.posts = cached.posts;
        store.loaded = true;
      }
    } catch {
      // cache illisible : on repart d'une liste vide
    }
  }
  store.lastSeenAt = safeGet(POSTS_LAST_SEEN_KEY) || null;
}

function time(value) {
  if (!value) return NaN;
  const t = Date.parse(value);
  return Number.isNaN(t) ? NaN : t;
}

// Tri du fil : epingles devant, puis du plus recent au plus ancien. Les dates
// passent par Date.parse et pas par une comparaison de chaines : le serveur peut
// rendre "...Z" comme "...+00:00" pour le meme instant.
function byPinnedThenRecent(a, b) {
  const pa = a.pinned ? 1 : 0;
  const pb = b.pinned ? 1 : 0;
  if (pa !== pb) return pb - pa;
  return (time(b.published_at) || 0) - (time(a.published_at) || 0);
}

function byOpenThenRecent(a, b) {
  const oa = a.status === 'open' ? 1 : 0;
  const ob = b.status === 'open' ? 1 : 0;
  if (oa !== ob) return ob - oa;
  return (time(b.published_at) || 0) - (time(a.published_at) || 0);
}

function byOldest(a, b) {
  return (time(a.published_at) || 0) - (time(b.published_at) || 0);
}

export function getUnreadCount(feed, lastSeenAt) {
  const list = Array.isArray(feed) ? feed : [];
  const seen = time(lastSeenAt);
  return list.filter((post) => {
    const at = time(post?.published_at);
    if (Number.isNaN(at)) return false;
    return Number.isNaN(seen) ? true : at > seen;
  }).length;
}

function buildSnapshot() {
  const posts = store.posts;
  const feed = posts.filter((p) => FEED_TYPES.includes(p?.type)).sort(byPinnedThenRecent);
  const byType = {
    news: posts.filter((p) => p?.type === 'news').sort(byPinnedThenRecent),
    live: posts.filter((p) => p?.type === 'live').sort(byPinnedThenRecent),
    release: posts.filter((p) => p?.type === 'release').sort(byPinnedThenRecent),
    known_issue: posts.filter((p) => p?.type === 'known_issue').sort(byOpenThenRecent),
    faq: posts.filter((p) => p?.type === 'faq').sort(byOldest),
  };
  return {
    posts,
    feed,
    byType,
    unreadCount: getUnreadCount(feed, store.lastSeenAt),
    lastSeenAt: store.lastSeenAt,
    loaded: store.loaded,
  };
}

function getSnapshot() {
  ensureInit();
  if (snapshotVersion !== version || snapshot === null) {
    snapshot = buildSnapshot();
    snapshotVersion = version;
  }
  return snapshot;
}

function subscribe(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function usePosts() {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

export async function loadPosts() {
  ensureInit();
  if (inflight) return inflight;
  const run = async () => {
    try {
      const res = await fetch(`${API_URL}/api/posts`);
      if (!res || !res.ok) return;
      const data = await res.json();
      if (!Array.isArray(data?.posts)) return;
      store.posts = data.posts;
      store.loaded = true;
      safeSet(POSTS_CACHE_KEY, JSON.stringify({ posts: data.posts, fetchedAt: new Date().toISOString() }));
      emit();
    } catch (err) {
      console.debug('posts: load failed', err?.message);
    }
  };
  // Le reset passe par un .finally externe : un fetch qui jetterait de facon
  // synchrone remettrait sinon inflight a null AVANT l'affectation, et la
  // promesse resolue resterait collee, condamnant tous les appels suivants.
  inflight = run().finally(() => {
    inflight = null;
  });
  return inflight;
}

export function markPostsSeen() {
  ensureInit();
  // N'emet que si la marque change quelque chose : sinon chaque ouverture du hub
  // relancerait un rendu de l'app pour rien.
  if (store.lastSeenAt && getSnapshot().unreadCount === 0) return;
  const now = new Date().toISOString();
  const previous = time(store.lastSeenAt);
  if (!Number.isNaN(previous) && Date.parse(now) <= previous) return;
  store.lastSeenAt = now;
  safeSet(POSTS_LAST_SEEN_KEY, now);
  emit();
}

// "1.0.1 (104)" -> "1.0.1".
function versionPart(appVersion) {
  return String(appVersion ?? '').trim().split(' ')[0];
}

export function releaseForVersion(posts, appVersion) {
  const wanted = versionPart(appVersion);
  if (!wanted) return null;
  const list = Array.isArray(posts) ? posts : [];
  return (
    list.find(
      (p) => p?.type === 'release' && p?.published_at && String(p.version ?? '').trim() === wanted
    ) || null
  );
}

export function shouldShowUpdateToast(posts, appVersion) {
  const release = releaseForVersion(posts, appVersion);
  if (!release) return false;
  return safeGet(POSTS_LAST_TOAST_KEY) !== versionPart(appVersion);
}

export function markUpdateToastShown(appVersion) {
  const wanted = versionPart(appVersion);
  if (!wanted) return;
  safeSet(POSTS_LAST_TOAST_KEY, wanted);
}

export function resetPostsStoreForTests() {
  store.posts = [];
  store.lastSeenAt = null;
  store.loaded = false;
  initialized = false;
  inflight = null;
  version = 0;
  snapshot = null;
  snapshotVersion = -1;
  listeners.clear();
}
